import Client from './client';
import { API_BASE } from './config';
import { getToken, useAuthStore } from '../stores/authStore';
import { tokenExpiresAt } from './token';

// Create a singleton client instance with credentials: 'include' for cookies. The
// generated client binds `fetch` when its module loads, before the interceptor
// below wraps it, so it is handed one that reads window.fetch at call time:
// without it a 401 from a typed call was never refreshed.
export const client = new Client(API_BASE as any, {
  fetcher: (input, init) => window.fetch(input, init),
  requestInit: { credentials: 'include' as RequestCredentials },
  auth: () => {
    const token = getToken();
    return {
      authorization: token ? `Bearer ${token}` : '',
      tenantId: '',
    };
  },
});

/** A failed call as the page says it: an ended session in plain words, else the server's message. */
export function errorText(err: unknown): string {
  if ((err as { code?: string } | null)?.code === 'unauthenticated') return 'Your session ended. Sign in again to carry on.';
  return err instanceof Error ? err.message : String(err);
}

// Export for direct access to typed service clients
export const { chat, zoom: account, connection, database, validation } = client;

// =============================================================================
// Global 401 interceptor with silent refresh
// =============================================================================

let refreshPromise: Promise<boolean> | null = null;

/** One refresh at a time, shared by the 401 interceptor and ensureFreshToken. */
function refreshOnce(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = useAuthStore.getState().refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Refresh the access token now if it expires within `minValidityMs`. Call before opening a
 * WebSocket: a 401 on its handshake can't be retried the way the fetch interceptor below retries,
 * so a chat message sent after the tab sat idle past the token's 15 minutes would just fail.
 */
export async function ensureFreshToken(minValidityMs = 60_000): Promise<void> {
  const token = getToken();
  if (!token) return;
  const expiresAt = tokenExpiresAt(token);
  if (expiresAt !== null && expiresAt - Date.now() > minValidityMs) return;
  await refreshOnce();
}

const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const response = await originalFetch(...args);

  if (response.status === 401) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url ?? '';
    if (!url.startsWith(API_BASE)) return response;

    // Don't retry refresh calls themselves
    if (url.includes('/auth/refresh')) return response;

    // Attempt silent refresh (deduplicated — only one refresh at a time)
    const refreshed = await refreshOnce();
    if (refreshed) {
      // Retry original request with new token
      const newToken = getToken();
      const [input, init] = args;
      const retryInit = { ...(init || {}) };
      // Headers, not a spread: the typed client sends `authorization` in lower
      // case, and a second `Authorization` key would join both tokens in one header.
      const headers = new Headers(retryInit.headers);
      headers.set('Authorization', newToken ? `Bearer ${newToken}` : '');
      retryInit.headers = headers;
      retryInit.credentials = 'include';
      return originalFetch(input, retryInit);
    }

    // Refresh failed — clear state, let ProtectedRoute redirect
    useAuthStore.setState({ token: null, user: null, hasZoomConnection: false });
  }

  return response;
};
